
import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "./ui/table";
import { Button } from "./ui/button";
import {
  Download,
  Search,
  Filter,
  MoreHorizontal,
  Eye,
  Trash2,
  FileText,
  Loader2,
  CheckCircle2,
  CalendarDays,
  CreditCard
} from "lucide-react";
import { isPaidStatus, getRegStatusInfo } from "@/lib/registration-utils";
import { Input } from "./ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import api from "../src/api";
import { toast } from "sonner";
import { RegistrationDetailsModal } from "./registration-details-modal";
import { useAuth } from "./auth-context";
import { PERMISSIONS } from "../../shared/permissions";

interface RegistrationTableProps {
  eventId?: string; // Optional initially, but required for data fetching
  axisFilter?: 'ALL' | 'cientifico' | 'cultural' | 'innovacion';
}

// Interface matching backend response
interface Registration {
  id: string;
  folio: string;
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
  correo: string;
  role: 'ATTENDEE' | 'SPEAKER';
  tipoParticipante: string;
  institucion: string;
  status: string; // PENDING, PAID, etc.
  createdAt: string;
  paidAt?: string | null;
  syswebReference?: string | null;
  paymentAmount?: string | number | null;
  // ... other fields
}

interface RegistrationsListResponse {
  data: Registration[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export function RegistrationTable({ eventId, axisFilter = 'ALL' }: RegistrationTableProps) {
  const { user } = useAuth();
  const canDelete = user?.isSuperAdmin || user?.permissions?.includes(PERMISSIONS.DELETE_REGISTRATION);

  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });
  const [selectedRegId, setSelectedRegId] = useState<string | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [eventId, axisFilter]);

  // Fetch registrations when eventId changes or on mount
  useEffect(() => {
    if (!eventId) {
      setRegistrations([]);
      return;
    }

    const fetchRegistrations = async () => {
      setIsLoading(true);
      try {
        const { data } = await api.get<RegistrationsListResponse>(`/registrations`, {
          params: {
            eventId,
            page,
            limit: 20,
            ...(axisFilter !== 'ALL' ? { axis: axisFilter } : {}),
          }
        });
        setRegistrations(Array.isArray(data) ? data : data.data);
        setMeta(Array.isArray(data)
          ? { page: 1, limit: data.length, total: data.length, totalPages: 1, hasNext: false, hasPrev: false }
          : data.meta
        );
      } catch (error) {
        console.error("Failed to fetch registrations", error);
        toast.error("Error al cargar registros");
      } finally {
        setIsLoading(false);
      }
    };

    fetchRegistrations();
  }, [eventId, axisFilter, page]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredRegistrations = registrations.filter(reg => {
    const fullName = `${reg.nombre} ${reg.apellidoPaterno} ${reg.apellidoMaterno}`.toLowerCase();
    const term = searchTerm.toLowerCase();
    return fullName.includes(term) || reg.folio.toLowerCase().includes(term) || reg.correo.toLowerCase().includes(term);
  });

  const handleDownloadCsv = async () => {
    if (!eventId) return;
    try {
      const response = await api.get(`/registrations/export/csv`, {
        params: {
          eventId,
          ...(axisFilter !== 'ALL' ? { axis: axisFilter } : {}),
        },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `registros-${eventId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error("Error al descargar CSV");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("¿Estás seguro de eliminar este registro?")) return;
    try {
      await api.delete(`/registrations/${id}`);
      setRegistrations(prev => prev.filter(r => r.id !== id));
      toast.success("Registro eliminado");
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const getStatusBadge = (status: string) => {
    const info = getRegStatusInfo(status);

    // El mapeo de estilos sigue en el componente
    let className = "";
    switch (info.kind) {
      case 'pending': className = "bg-yellow-50 text-yellow-700 border-yellow-200"; break;
      case 'paid': className = "bg-green-50 text-green-700 border-green-200"; break;
      case 'approved_to_pay': className = "bg-[#1e3a5f]/10 text-[#1e3a5f] border-[#1e3a5f]/30"; break;
      case 'rejected': className = "bg-red-50 text-red-700 border-red-200"; break;
      case 'review': className = "bg-orange-50 text-orange-700 border-orange-200"; break;
      default: className = "";
    }

    return (
      <Badge variant="outline" className={`${className} flex items-center gap-1`}>
        {info.showCheck && <CheckCircle2 className="h-3 w-3" />}
        {info.label}
      </Badge>
    );
  };

  const getRoleBadge = (role: string) => {
    if (role === 'SPEAKER') {
      return <Badge className="bg-[#c9a227] text-white hover:bg-[#c9a227]/90">Ponente</Badge>;
    }
    return <Badge className="bg-[#1e3a5f] text-white hover:bg-[#1e3a5f]/90">Asistente</Badge>;
  };

  if (!eventId) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-gray-400 border-2 border-dashed rounded-lg">
        <Filter className="h-10 w-10 mb-2" />
        <p>Selecciona un evento para ver sus registros</p>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0 pb-6">
        <div>
          <CardTitle>Listado de Inscripciones</CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            {meta.total} registros encontrados
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Buscar por nombre, folio..."
              className="pl-9 w-full sm:w-[250px]"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <Button variant="outline" onClick={handleDownloadCsv}>
            <Download className="mr-2 h-4 w-4" /> Exportar CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--unach-dark-blue)' }} />
          </div>
        ) : filteredRegistrations.length === 0 ? (
          <div className="text-center py-10 text-gray-500">
            No se encontraron registros.
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">Folio</TableHead>
                  <TableHead>Participante</TableHead>
                  <TableHead>Rol / Institución</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Pago / Detalles</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRegistrations.map((reg) => (
                  <TableRow key={reg.id}>
                    <TableCell className="font-mono text-[10px] text-gray-400">
                      {reg.folio}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-semibold text-sm text-[#0f2744]">
                          {reg.nombre} {reg.apellidoPaterno}
                        </span>
                        <span className="text-[11px] text-gray-500">{reg.correo}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {getRoleBadge(reg.role)}
                        <span className="text-[10px] text-gray-400 truncate max-w-[120px]" title={reg.institucion}>
                          {reg.institucion}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(reg.status)}
                    </TableCell>
                    <TableCell>
                      {isPaidStatus(reg.status) ? (
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-1 text-[11px] font-medium text-gray-700">
                            <CalendarDays className="h-3 w-3 text-gray-400" />
                            {reg.paidAt ? new Date(reg.paidAt).toLocaleDateString() : 'S/F'}
                          </div>
                          {reg.syswebReference && (
                            <div className="flex items-center gap-1 text-[10px] text-gray-500">
                              <CreditCard className="h-3 w-3 text-gray-400" />
                              Ref: {reg.syswebReference}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-[11px] text-gray-400 italic">No conciliado</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Abrir menú</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Acciones</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => { setSelectedRegId(reg.id); setIsDetailsOpen(true); }}>
                            <Eye className="mr-2 h-4 w-4" /> Ver Detalles
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigator.clipboard.writeText(reg.folio)}>
                            <FileText className="mr-2 h-4 w-4" /> Copiar Folio
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {canDelete && (
                            <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(reg.id)}>
                              <Trash2 className="mr-2 h-4 w-4" /> Eliminar
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <div className="mt-4 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Pagina {meta.page} de {Math.max(meta.totalPages, 1)}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!meta.hasPrev || isLoading}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!meta.hasNext || isLoading}
              onClick={() => setPage((prev) => prev + 1)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      </CardContent>

      <RegistrationDetailsModal
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        registrationId={selectedRegId}
      />
    </Card>
  );
}
